import type {ReactElement} from 'react';
import React, {useState} from 'react';
import {onOpenReplaceAudio, openTrimAudioModalForSelectedAudioItem} from '@Components/poster-editor/library/poster-editor-open-modals';
import {ControlItems} from '@Components/contextual-menu/components/control-items';
import {ContextualMenuReplaceMediaControls} from '@Components/contextual-menu/components/contextual-menu-replace-media-controls';
import {ContextualMenuSliderOptions} from '@Components/contextual-menu/components/contextual-menu-slider-options';
import {ContextualMenuToggleOption} from '@Components/contextual-menu/components/contextual-menu-toggle-option';
import {AudioControlsFadeControls} from '@Components/poster-editor/components/poster-editing-side-panel/components/audio-item-controls/components/audio-controls-fade-controls';
import {useSelectedAudioItem} from '@Hooks/poster-editor/useSelectedAudioItem';
import {isLocal} from '@Libraries/environment-library';
import {AudioItemVolumeOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/audio-item-volume-option/audio-item-volume-option';
import {AudioSpeedOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/audio-speed-option/audio-speed-option';
import styles from './audio-item-controls.module.scss';
import {TimelineAudioOnboarding} from '@Components/poster-editor/components/poster-editing-side-panel/components/timeline-audio-onboarding/timeline-audio-onboarding';
import {AudioItemGenerateAiSubtitlesOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/generate-ai-subtitles-option/components/audio-item-generate-ai-subtitles-option/audio-item-generate-ai-subtitles-option';

enum AudioControls {
  TRIM = 'trim',
  REPLACE = 'replace',
  DUPLICATE = 'duplicate',
  DELETE = 'delete',
  GENERATE_AI_TRANSCRIPT = 'generate_ai_transcript',
}

export function AudioItemControls(): ReactElement<any> | null {
  const selectedAudioItem = useSelectedAudioItem();
  const [isReplaceOptionEnabled, setIsReplaceOptionEnabled] = useState(false);

  if (!selectedAudioItem) {
    throw new Error('No selected audio item');
  }

  const updateAudioItemPlayCycles = (playCycles: number, undoable: boolean): void => {
    void selectedAudioItem.updateFromObject(
      {
        audioPlayer: {
          playCycles,
        },
      },
      {
        undoable,
      }
    );
  };

  const onToggleFade = (): void => {
    void selectedAudioItem.onToggleFade();
  };

  const replaceAudioToggled = (): void => {
    setIsReplaceOptionEnabled(true);
  };

  const getReplaceOptions = (): ReactElement<any> => {
    return (
      <ContextualMenuReplaceMediaControls
        controlsClassName={styles.replaceOption}
        className={`_full-width ${styles.replaceOption}`}
        id="audio-item-replace-media-controls"
        onClick={(id) => {
          const currentAudioItemId = window.posterEditor?.whiteboard?.audioClips.getSelectedAudioItem()?.uid;
          if (currentAudioItemId) {
            onOpenReplaceAudio(id, currentAudioItemId);
          }
        }}
        onCancel={() => {
          setIsReplaceOptionEnabled(false);
        }}
      />
    );
  };

  const getTrimButton = (): ReactElement<any> => {
    return (
      <ControlItems
        className={styles.stretchToFlex1}
        key={AudioControls.TRIM}
        id={AudioControls.TRIM}
        icon="icon-cut"
        text={window.i18next.t('pmwjs_trim')}
        onClick={(): void => {
          openTrimAudioModalForSelectedAudioItem();
        }}
      />
    );
  };

  const getDuplicateButton = (): ReactElement<any> => {
    return (
      <ControlItems
        className={styles.stretchToFlex1}
        key={AudioControls.DUPLICATE}
        id={AudioControls.DUPLICATE}
        icon="icon-copy"
        text={window.i18next.t('pmwjs_duplicate')}
        onClick={() => {
          window.posterEditor?.whiteboard?.audioClips.duplicateAudio(selectedAudioItem?.uid);
        }}
      />
    );
  };

  const getReplaceButton = (): ReactElement<any> => {
    return (
      <ControlItems
        className={styles.stretchToFlex1}
        key={AudioControls.REPLACE}
        id={AudioControls.REPLACE}
        icon="icon-replace"
        text={window.i18next.t('pmwjs_replace')}
        onClick={() => {
          replaceAudioToggled();
        }}
      />
    );
  };

  const getDeleteButton = (): ReactElement<any> => {
    return (
      <ControlItems
        className={styles.stretchToFlex1}
        key={AudioControls.DELETE}
        id={AudioControls.DELETE}
        icon="icon-delete"
        text={window.i18next.t('pmwjs_delete')}
        iconClasses="color-danger-100"
        onClick={(): void => {
          window.posterEditor?.whiteboard?.audioClips.deleteSelectedAudioItem();
        }}
      />
    );
  };

  const getAiSubtitlesOption = (): ReactElement<any> => {
    return <AudioItemGenerateAiSubtitlesOption key={AudioControls.GENERATE_AI_TRANSCRIPT} />;
  };

  const getVolumeControlSlider = (): ReactElement<any> => {
    return <AudioItemVolumeOption className="spacing-m-t-6" />;
  };

  const getSpeedControlSlider = (): ReactElement<any> => {
    return <AudioSpeedOption className="spacing-m-t-6" />;
  };

  const getPlayCyclesControlSlider = (): ReactElement<any> => {
    return (
      <ContextualMenuSliderOptions
        className="spacing-m-t-6"
        range={{
          min: 0.25,
          max: 4,
        }}
        transformDisplay={{
          min: 0.25,
          max: 4,
        }}
        step={0.1}
        value={selectedAudioItem.audioPlayer.playCycles}
        label="PlayCycles (Dev Only)"
        onChange={(val: number): void => {
          updateAudioItemPlayCycles(val, false);
        }}
        onChangeEnded={(val: number): void => {
          updateAudioItemPlayCycles(val, true);
        }}
        id="volume"
      />
    );
  };

  const getFadeControls = (): ReactElement<any> => {
    return (
      <ContextualMenuToggleOption
        className="spacing-m-t-6"
        subOptionsClassname={styles.fadeControls}
        id="fade"
        label={window.i18next.t('pmwjs_fade')}
        isChecked={selectedAudioItem.audioPlayer.hasFade()}
        onClick={onToggleFade}
      >
        {selectedAudioItem.audioPlayer.hasFade() ? <AudioControlsFadeControls className="_full-width _full-height spacing-p-0 spacing-m-0" /> : null}
      </ContextualMenuToggleOption>
    );
  };

  return (
    <>
      <ul key="audio-delete-option" className="spacing-m-l-0 spacing-m-b-0 flex-column-align-center flex-justify-between _full-width">
        {isReplaceOptionEnabled ? (
          getReplaceOptions()
        ) : (
          <div className="flexbox flex-justify-between _full-width ">
            {getTrimButton()}
            {getReplaceButton()}
            {getDuplicateButton()}
            {getDeleteButton()}
          </div>
        )}
        <div className="_full-width spacing-m-t-4 spacing-m-b-6">
          {getAiSubtitlesOption()}
          {getVolumeControlSlider()}
          {getSpeedControlSlider()}
          {isLocal() ? getPlayCyclesControlSlider() : null}
          <div className={styles.fadeOptionsWrapper}>{getFadeControls()}</div>
        </div>
      </ul>
      <TimelineAudioOnboarding />
    </>
  );
}
